import React from 'react';
import { Clock, Warning, FileX, UsersThree, ArrowRight } from '@phosphor-icons/react';
import { usePersona, personas, PersonaId } from './PersonaContext';
interface PainCard {
  title: string;
  desc: string;
  cost: string;
  icon: typeof Clock;
  personaTags: Array<Exclude<PersonaId, null>>;
}
const pains: PainCard[] = [{
  title: 'Payroll takes too long',
  desc: 'Awards, super, STP and leave entitlements quietly eat 6 to 10 hours every fortnight.',
  cost: 'Cost of doing nothing: 150+ hours a year lost to admin.',
  icon: Clock,
  personaTags: ['sme', 'accountant']
}, {
  title: 'Bookkeeping errors create risk',
  desc: 'One miscoded transaction snowballs into a BAS issue, a tax-time scramble, or a director liability.',
  cost: 'Cost of doing nothing: ATO penalties, director liability, restated accounts.',
  icon: Warning,
  personaTags: ['sme', 'accountant']
}, {
  title: 'Financial reports arrive too late',
  desc: 'Hiring, pricing and cash flow decisions get made on numbers that are 6 weeks old, or no numbers at all.',
  cost: 'Cost of doing nothing: gut-feel decisions that miss the runway window.',
  icon: FileX,
  personaTags: ['sme', 'cfo']
}, {
  title: 'Hiring in-house adds cost and complexity',
  desc: 'Salary, super, software seats, supervision and leave cover, before the work even starts.',
  cost: 'Cost of doing nothing: $80k+ fully loaded for a bookkeeper, before quality is proven.',
  icon: UsersThree,
  personaTags: ['sme', 'accountant', 'cfo']
}];
export function Pain() {
  const {
    persona
  } = usePersona();
  const headline = persona ? `What is slowing down ${persona === 'sme' ? 'your business' : persona === 'accountant' ? 'your firm' : 'your decision-making'}.` : 'Still managing books, payroll and reporting when you should be growing the business?';
  const personaPain = persona ? personas[persona].primaryPain : null;
  return <section id="pain" className="py-16 lg:py-24 bg-off-white" data-id="element-315">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-316">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-317">
          <p className="font-mono text-[11px] tracking-wider uppercase text-knight-orange mb-3" data-id="element-318">
            The hidden cost of the back office
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-charcoal leading-tight mb-5" data-id="element-319">
            {headline}
          </h2>
          {personaPain && persona && <div className="bg-white border border-knight-orange/30 rounded-md p-5 text-left max-w-2xl mx-auto" data-id="element-320">
              <div className="inline-flex items-center px-2 py-0.5 rounded bg-knight-orange/10 mb-3" data-id="element-321">
                <span className="font-mono text-[10px] uppercase tracking-wider text-knight-orange" data-id="element-322">
                  For / {personas[persona].shortLabel}
                </span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-wider text-gray-500 block mb-2" data-id="element-323">
                Sound familiar?
              </span>
              <p className="text-lg text-charcoal leading-relaxed" data-id="element-324">
                {personaPain}
              </p>
            </div>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-14 stagger-children" data-id="element-325">
          {pains.map((pain, i) => {
          const isHighlighted = persona && pain.personaTags.includes(persona);
          return <div key={i} className={`bg-white p-7 lg:p-8 rounded-md border transition-all duration-500 ease-out-expo ${isHighlighted ? 'border-knight-orange/40 ring-1 ring-knight-orange/10' : 'border-hairline'}`} data-id="element-326">
                <div className="w-11 h-11 bg-off-white rounded-md flex items-center justify-center mb-5" data-id="element-327">
                  <pain.icon size={22} weight="duotone" className="text-knight-orange" data-id="element-328" />
                </div>
                <h3 className="text-xl font-heading font-bold text-charcoal mb-3" data-id="element-329">
                  {pain.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-330">
                  {pain.desc}
                </p>
                <p className="font-mono text-[11px] text-charcoal border-t border-hairline pt-3" data-id="element-331">
                  <span className="text-knight-orange" data-id="element-332">/ </span>
                  {pain.cost}
                </p>
              </div>;
        })}
        </div>

        <div className="max-w-4xl mx-auto text-center" data-id="element-333">
          <p className="text-xl sm:text-2xl text-charcoal font-medium leading-relaxed mb-8" data-id="element-334">
            If your back office is eating your week, the cost is bigger than
            admin time. It affects cash flow clarity, compliance confidence,
            staff productivity, and the decisions you make as an owner.
          </p>
          <a href="#lead" className="inline-flex items-center px-8 py-4 bg-knight-orange text-white font-semibold rounded-md hover:bg-knight-orange-hover transition-colors duration-300 ease-out-expo" data-id="element-335">
            Show me how Knight fixes this
            <ArrowRight size={18} weight="regular" className="ml-2" data-id="element-336" />
          </a>
          <p className="font-mono text-[11px] tracking-wide text-gray-500 uppercase mt-4" data-id="element-337">
            No startup costs / No lock-in / Fixed monthly fees / Cancel anytime
          </p>
        </div>
      </div>
    </section>;
}